import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import GenService from '../services/GenService';

const BUSY_STATUSES = new Set([
    'initializing',
    'loading',
    // The search is over and points are being allocated. The gauge holds still
    // through this; it just changes what the button says.
    'finalizing',
]);

/**
 * What a generation the browser declined fails with.
 *
 * The code is what the UI keys its message off, in five languages. The backend
 * answers `POST gen/` with the same code, which is how this looked when a
 * decline still went through the server first.
 */
export const CODE_SERVER_SOLVE_OFF = 'SERVER_SOLVE_OFF';

function declinedError(reason) {
    const err = new Error(`The browser could not run this generation${reason ? ` (${reason})` : ''}.`);
    err.code = CODE_SERVER_SOLVE_OFF;
    err.reason = reason;
    return err;
}

/**
 * One generation at a time, solved in the browser.
 *
 * `start` runs `attemptLocal` (see useGenerationRun) and settles through
 * `onDone` or `onError`. A decline is reported to the server — the dashboard's
 * fallback metrics are made of these reasons — and fails with
 * `CODE_SERVER_SOLVE_OFF`; the generation cap fails with its own typed error.
 * There is no other solver to hand over to.
 */
export function useGenerationJob(options = {}) {
    const {
        reportDecline = (req, reason) => GenService.reportLocalDecline(req, reason),
    } = options;

    const [status, setStatus] = useState('idle');
    // True once a run commits to solving: the gauge keeps still while the
    // islands are prepared, and only then starts to fill.
    const [isLocalRun, setIsLocalRun] = useState(false);
    const [error, setError] = useState('');

    const runTokenRef = useRef(0);
    const isGeneratingRef = useRef(false);
    const abortControllerRef = useRef(null);

    const isRunActive = useCallback((token) => (
        runTokenRef.current === token && isGeneratingRef.current
    ), []);

    const reset = useCallback(() => {
        runTokenRef.current += 1;
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        isGeneratingRef.current = false;
        setStatus('idle');
        setError('');
    }, []);

    // Pressing stop has to reach the workers, which the abort signal does.
    const stop = useCallback(async () => {
        reset();
    }, [reset]);

    const start = useCallback(async ({
        req,
        timeLimit = 10,
        attemptLocal,
        onDone,
        onError,
    }) => {
        if (isGeneratingRef.current) {
            await stop();
        } else {
            reset();
        }

        const runToken = Date.now();
        runTokenRef.current = runToken;
        isGeneratingRef.current = true;
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const { signal } = abortController;

        setError('');
        setIsLocalRun(false);
        setStatus('initializing');

        const finish = () => {
            abortControllerRef.current = null;
            isGeneratingRef.current = false;
            setStatus('idle');
        };
        const fail = (err) => {
            finish();
            setError(err?.message || 'Generation failed');
            if (typeof onError === 'function') onError(err, null);
        };

        // Why the browser declined, when it did. The reason is produced here and
        // nowhere else, so not reporting it is the same as never computing it.
        let declineReason = '';
        let result = null;
        if (typeof attemptLocal === 'function') {
            try {
                result = await attemptLocal({
                    signal,
                    timeLimit: Number(timeLimit) || 10,
                    onFallback: (reason) => { declineReason = String(reason || ''); },
                    // Solving, but still preparing: the gauge switches to its
                    // local bands while the label stays on "initializing".
                    onLocalStart: () => {
                        if (isRunActive(runToken)) setIsLocalRun(true);
                    },
                    // Preparing is done and the budget has started.
                    onSearchStart: () => {
                        if (isRunActive(runToken)) setStatus('loading');
                    },
                    onFinalize: () => {
                        if (isRunActive(runToken)) setStatus('finalizing');
                    },
                });
            } catch (err) {
                if (!isRunActive(runToken) || signal.aborted) return null;
                // The generation cap and an unreachable server are not
                // declines: the caller says which, and there is nothing to report.
                if (err?.status === 429 || err?.serverDown) {
                    fail(err);
                    return null;
                }
                result = null;
                if (!declineReason) declineReason = 'local solve failed';
            }
        } else {
            declineReason = 'no local solver';
        }

        if (!isRunActive(runToken) || signal.aborted) return null;
        if (result) {
            finish();
            if (typeof onDone === 'function') onDone(result);
            return result;
        }

        reportDecline(req, declineReason);
        fail(declinedError(declineReason));
        return null;
    }, [isRunActive, reportDecline, reset, stop]);

    useEffect(() => () => {
        reset();
    }, [reset]);

    const isGenerating = useMemo(() => BUSY_STATUSES.has(status), [status]);

    return {
        status,
        error,
        isGenerating,
        isLocalRun,
        start,
        stop,
        reset,
    };
}

export default useGenerationJob;
